import { createContext, useContext, useRef } from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'

import { useStore } from 'zustand'

import { createEngagementCoordinationStore } from '../hooks/engagement/engagementCoordinationStore'
import type {
  EngagementCoordinationStore,
  EngagementCoordinationStoreState,
} from '../hooks/engagement/engagementCoordinationStore'

/**
 * Holds the per-list coordination store instance. Comments and Mentions each
 * mount their own provider, so the two lists keep independent expansion and
 * resolution state while sharing one implementation.
 */
const EngagementCoordinationContext =
  createContext<EngagementCoordinationStore | null>(null)

EngagementCoordinationContext.displayName = 'EngagementCoordinationContext'

type EngagementCoordinationProviderProps = {
  children: ReactNode
}

/**
 * Creates one coordination store for the subtree and exposes it via context.
 * The store is created once (lazy ref init) and never recreated, so cards can
 * subscribe to it across the list's lifetime.
 */
export const EngagementCoordinationProvider = ({
  children,
}: EngagementCoordinationProviderProps): JSX.Element => {
  const storeRef = useRef<EngagementCoordinationStore>()
  if (!storeRef.current) {
    storeRef.current = createEngagementCoordinationStore()
  }

  return (
    <EngagementCoordinationContext.Provider value={storeRef.current}>
      {children}
    </EngagementCoordinationContext.Provider>
  )
}

/**
 * The raw store instance — for the list's wiring hook, which reads state
 * imperatively (`store.getState()`) and calls actions without subscribing, so
 * a navigation change never re-renders the whole list.
 */
export const useEngagementCoordinationStoreApi =
  (): EngagementCoordinationStore => {
    const store = useContext(EngagementCoordinationContext)
    if (!store) {
      throw new Error(
        'useEngagementCoordinationStoreApi must be used within an ' +
          'EngagementCoordinationProvider.',
      )
    }
    return store
  }

/**
 * Selector subscription into the coordination store. Used by the per-list-item
 * wrappers to read `expanded` / `isLocallyResolved`; zustand re-renders a
 * subscriber only when its selected slice changes, so a navigation change
 * re-renders only the cards whose expanded state actually flipped.
 */
export function useEngagementCoordinationStore<T>(
  selector: (state: EngagementCoordinationStoreState) => T,
): T {
  return useStore(useEngagementCoordinationStoreApi(), selector)
}
